/**
 * LastGiftOverlay — the shared, purely-presentational "last sent gift" banner:
 * sender avatar + "{name} sent a gift" + rounded coin amount + coin icon + gift
 * thumbnail + optional `x{giftInstances}` multiplier. Flutter ships two
 * near-identical copies of this widget — the video-call `_lastGift()`
 * (`video_call_screen.dart:551-691`) and the live-streaming `lastGift()`
 * (`live_streaming_screen.dart:922-1056`) — so the web keeps ONE shared component
 * and each surface wraps it with its own copy source, positioning, and qa id
 * (`workflow/lessons.md` "reuse beats literal porting"; `conventions.md` "New
 * responsibility → new owner file"). The video-call `VideoCallLastGift` and the
 * live-streaming overlay both delegate here.
 *
 * This component is PURE and prop-driven: the CONSUMER owns the current-user
 * decision and passes the finished `title` in (already substituting "You" for the
 * self-sent case, Flutter video-call `:562-566` / live `:930-934`); this
 * component never reads the firebase uid itself. It also takes a `style` override
 * so each surface positions the banner where its Flutter twin does (video-call:
 * left-edge, vertically centred, `:552-554`; live: left-padded 16 above the chat
 * band, `:941`).
 *
 * Flutter → web parity notes:
 *  - `null` gift renders nothing (Flutter `:558-560` / `:926-928`).
 *  - Fade: NO animated fade runs, on either platform — the overlay pops in and
 *    pops out. Flutter's `_latestLiveGiftInfoValueNotifier` swaps between an empty
 *    `Container()` and the `AnimatedOpacity` on every show/clear (`:558-560`), so
 *    the widget type changes each time and the `AnimatedOpacity(700ms, bounceIn,
 *    :568-571)` never gets a same-instance opacity delta to animate. Web matches:
 *    the element early-returns `null` when `gift == null` and only mounts once a
 *    gift arrives (already at `opacity:1`), then unmounts when the gift is nulled,
 *    so there is no rendered 0-frame to transition from. The `opacity` prop is kept
 *    as a 1:1 mirror of Flutter's `_latestLiveGiftOpacity` state field (view-model
 *    parity), but drives no visible transition in practice.
 *  - Pill: Flutter `backgroundColorLight.withValues(alpha: .2)` + `borderRadius 32`
 *    + padding L8/T4/R8/B4 (`:578-591` / `:946-957`). Web: `theme.lastGiftPillBackground`
 *    (translucent dark = Flutter native `backgroundColorLight #171717 @ .2`, so the
 *    gold coin icon keeps contrast — NOT the gold `exclusiveTransparent`)
 *    + `responsive.borderRadius['2xl']` (32) + `spacing.xs` padding.
 *  - Avatar: Flutter `CircleBorderAvatar` size 30 (`latestLiveGiftUserProfileSize`)
 *    with `generateUserProfileImageUrl(userId)`. Web: `CachedImage` sized
 *    `responsive.avatar.small` (32 ≈ Flutter 30).
 *  - Coin amount: Flutter `MathUtils.roundDoubleTwoDecimals(coins)` (`:630` / `:995`)
 *    — drops decimals for whole coins (`100 → "100"`, `175.5 → "175.50"`). This is a
 *    DIFFERENT helper from `GiftBubble`'s `roundTwoDecimals` (always `"100.00"`);
 *    the web helper is `MathUtils.roundDoubleTwoDecimals` (`mathUtils.ts:179`).
 *  - Coin icon: Flutter `assets/images/coins.png` size 12
 *    (`notificationCoinIconSize`, `:637-646` / `:1002-1011`), tinted
 *    `AppColors.exclusive` (the gold "coins = gold" cue) in both twins. Web:
 *    `AssetIcon` sized `responsive.icon.xs` (12), `color={theme.exclusive}`.
 *  - Gift thumbnail: Flutter `LatestLiveGiftInfo.path` = `Env.giftsBucket +
 *    giftThumbnail` (`latest_live_gift_info.dart:15`) sized 34 (`latestLiveGiftSize`,
 *    `:661` / `:1026-1027`). Web: `GiftUtils.getGiftPath(giftThumbnail)` (the shared
 *    gift-URL helper, same as `GiftBubble`) sized `responsive.icon.xlarge` (32 ≈
 *    Flutter 34, closest token). Guarded on a non-empty `giftThumbnail` like
 *    `GiftBubble`.
 *  - Multiplier: Flutter renders `x{giftInstances}` OUTSIDE the pill only when
 *    `giftInstances > 1` (strict, `:678-684` / `:1043-1048`), styled
 *    `liveBroadcastingGiftInstancesStyle` (fontSize 24, italic). Web:
 *    `responsive.font['3xl']` (24) + `fontStyle: italic` + `theme.text.primary`.
 *
 * Theme/responsive/l10n are mandatory (`presentation.md`) — no hardcoded colors,
 * pixels, or strings (the localized `title` is built by the consumer). The wrapper
 * is `pointerEvents: none` so it never blocks the video controls beneath it. The
 * root carries a caller-supplied `qaAttr` id.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { MathUtils } from '@/domain/utils/mathUtils';
import { UserUtils } from '@/domain/utils/userUtils';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';

export interface LastGiftOverlayProps {
  /**
   * The gift to display, or `null` to render nothing (Flutter `:558-560` /
   * `:926-928`). Mirrors the `latestLiveGift` view-model field the consuming hook
   * exposes.
   */
  gift: LatestLiveGiftInfoEntity | null;
  /**
   * Current fade opacity (Flutter `_latestLiveGiftOpacity`). `1` while the gift is
   * shown, `0` once the display window elapses. A 1:1 view-model mirror; drives no
   * visible CSS transition (see the docblock's Fade note).
   */
  opacity: number;
  /**
   * The finished, localized banner title — already substituting "You" for the
   * self-sent case (Flutter `"$username sent a gift"`, `:615` / `:980`). The
   * CONSUMER owns the current-user decision and the l10n lookup.
   */
  title: string;
  /**
   * Positioning override merged onto the root flex row. Each surface positions the
   * banner where its Flutter twin sits: the video-call left-edge / vertically
   * centred placement (`:552-554`), or the live left-padded-above-the-chat-band
   * placement (`:941`).
   */
  style?: React.CSSProperties;
  /** Stable `data-qa-id` for the root (per-surface). */
  qaId: string;
}

export const LastGiftOverlay: React.FC<LastGiftOverlayProps> = ({
  gift,
  opacity,
  title,
  style,
  qaId,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // `null` gift renders nothing (Flutter `:558-560` / `:926-928`).
  if (gift == null) {
    return null;
  }

  // Coin amount: `MathUtils.roundDoubleTwoDecimals` (Flutter `:630` / `:995`) —
  // drops the decimals for whole coins (`100 → "100"`). NOT `roundTwoDecimals`.
  const coinsText = MathUtils.roundDoubleTwoDecimals(gift.coins);

  // Gift image URL: `GiftUtils.getGiftPath(giftThumbnail)` (Flutter
  // `LatestLiveGiftInfo.path`, `latest_live_gift_info.dart:15`). Guarded on a
  // non-empty thumbnail like `GiftBubble`.
  const giftImageUrl = gift.giftThumbnail ? GiftUtils.getGiftPath(gift.giftThumbnail) : '';

  return (
    <div
      {...qaAttr({ id: qaId })}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        // `opacity` mirrors Flutter's `_latestLiveGiftOpacity` field for view-model
        // parity; it is always `1` while the element is mounted (see docblock's Fade
        // note), so no CSS `transition` is attached — it would never fire.
        opacity,
        pointerEvents: 'none',
        ...style,
      }}
    >
      {/* Translucent rounded pill (Flutter `:578-591` / `:946-957`). */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.lastGiftPillBackground, // Flutter `backgroundColorLight.withValues(alpha: .2)` — translucent dark, so the gold coin icon keeps contrast
          borderRadius: responsive.borderRadius['2xl'],
          padding: responsive.spacing.xs,
        }}
      >
        {/* Sender avatar (Flutter `CircleBorderAvatar`, size 30, `:596-606` /
            `:962-971`). */}
        <CachedImage
          src={UserUtils.generateUserProfileImageUrl(gift.userId)}
          alt=""
          style={{
            width: responsive.avatar.small,
            height: responsive.avatar.small,
            borderRadius: responsive.borderRadius.round,
            objectFit: 'cover',
          }}
        />

        {/* Title + coin row (Flutter's middle column, `:607-652` / `:972-1017`). */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginLeft: responsive.spacing.xs,
          }}
        >
          <span
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.sm,
              textAlign: 'left',
            }}
          >
            {title}
          </span>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                color: theme.text.primary,
                fontSize: responsive.font.sm,
                textAlign: 'left',
              }}
            >
              {`${coinsText} `}
            </span>
            <AssetIcon
              path="/assets/images/coins.png"
              width={responsive.icon.xs}
              height={responsive.icon.xs}
              // Flutter tints this coin silhouette `AppColors.exclusive` (the gold
              // "coins = gold" cue) in both twins (`:1002-1011` / `:637-646`);
              // matches the sibling `LiveStreamingCoinsStat` pill icon.
              color={theme.exclusive}
            />
          </div>
        </div>

        {/* Gift thumbnail (Flutter `:653-673` / `:1018-1038`, size 34). */}
        {giftImageUrl && (
          <CachedImage
            src={giftImageUrl}
            alt={gift.giftName}
            style={{
              width: responsive.icon.xlarge,
              height: responsive.icon.xlarge,
              marginLeft: responsive.spacing.sm,
              marginRight: responsive.spacing.sm,
              objectFit: 'cover',
            }}
          />
        )}
      </div>

      {/* `x{giftInstances}` multiplier OUTSIDE the pill, only when strictly > 1
          (Flutter `:678-684` / `:1043-1048`). */}
      {gift.giftInstances > 1 && (
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font['3xl'],
            fontStyle: 'italic',
            textAlign: 'left',
          }}
        >
          {` x${gift.giftInstances}`}
        </span>
      )}
    </div>
  );
};
